import { geocode } from '@api/Addresses'
import { addLocation, snapshotLocalPickup } from '@api/Locations'
import { GooglePlacesSearch, ToolTips } from '@components'
import {
  FormInput,
  FormInputLabel,
  FormPickerInput,
  FormStatePicker,
  FormZipcodeInput,
  PickerProps,
  Text,
  Toast,
  typography,
} from '@elements'
import { getTimeZone } from '@helpers/getTimeZone'
import { LocalPickup, LocationTypes } from '@models/Location'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { Formik, FormikHelpers, FormikProps } from 'formik'
import { useCallback, useMemo } from 'react'
import { StyleSheet, View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'

import LoaderWithMessage from '../../../components/LoaderWithMessage'
import { AdminView } from '../../components/AdminView'
import { EditHeader } from '../../components/EditHeader'
import { LocationsAndZonesParamList } from '../../navigation/types'
import { ReturnStates, validateLocationEdit } from './editValidation'

import Colors from '@/constants/Colors'
import { GoogleAddressParser } from '@/constants/GoogleAddressParser'
import { useSnapshot } from '@/hooks/useSnapshot'
import { withAdminAuth } from '@/hooks/withAdminAuth'
import { setAdminNav } from '@/redux/actions/adminState'
import { adminFarmIdSelector, adminFarmSelector } from '@/redux/selectors'
import { locationsCollection } from '@api/framework/ClientCollections'
import { AccessRight, Permission } from '@helpers/Permission'
import { getShortState } from '@helpers/address'
import { addressSchemaForm } from '@helpers/builders/AddressBuilder'
import { errorToString } from '@helpers/helpers'
import { Resolved } from '@helpers/typescript'
import { Address, GooglePlace } from '@models/Address'
import { locAbbreviationSchema } from './helpers'

type FormType = Omit<Address, 'coordinate'> & {
  locationName: string
  abbreviation?: string
  type: LocalPickup['type']
}

const typeDropdownItems: PickerProps['items'] = [
  {
    label: 'Farm location',
    value: LocationTypes.FARM,
  },
  {
    label: 'Farm stand or store',
    value: LocationTypes.STAND,
  },
  {
    label: 'Farmers market',
    value: LocationTypes.MARKET,
  },
  {
    label: 'Community Location',
    value: LocationTypes.COMMUNITY,
  },
]

const setGooglePlace = (details: GooglePlace, handler: (newValues: Partial<FormType>) => void) => {
  if (!details.address_components) return
  try {
    const address = new GoogleAddressParser(details.address_components).getAddress()
    const newValues: Pick<FormType, 'locationName' | 'street1' | 'city' | 'state' | 'zipcode' | 'country'> = {
      locationName: details.name || '',
      street1: address.street1,
      city: address.city,
      state: address.state,
      zipcode: address.zipcode,
      country: address.country,
    }
    handler(newValues)
  } catch (error) {
    Toast(
      'The place could not be parsed correctly and may be missing an address field. You may try entering the address fields manually',
    )
  }
}

const locationValidationSchema: Yup.ObjectSchema<FormType> = addressSchemaForm.shape({
  locationName: Yup.string().trim().label('Location Name').required(),
  abbreviation: locAbbreviationSchema,
  type: Yup.mixed<LocalPickup['type']>()
    .required()
    .oneOf([LocationTypes.COMMUNITY, LocationTypes.FARM, LocationTypes.MARKET, LocationTypes.STAND]),
})

/** This screen will only be used for LocalPickup type locations */
function AddLocationScreenComp() {
  const navigation = useNavigation<StackNavigationProp<LocationsAndZonesParamList, 'AddLocation'>>()
  const dispatch = useDispatch()
  const { params } = useRoute<RouteProp<LocationsAndZonesParamList, 'AddLocation' | 'EditLocation'>>()
  const farmId = useSelector(adminFarmIdSelector)
  const farm = useSelector(adminFarmSelector)
  const isEdit = !!params?.id

  const {
    data: editLocation,
    loading,
    error,
  } = useSnapshot('snapshotLocalPickup', snapshotLocalPickup, [params?.id, farmId], isEdit, {
    onStateChange({ data }) {
      dispatch(setAdminNav({ location: data }))
    },
  })

  const initialValues = useMemo<FormType>(
    () =>
      !isEdit || !editLocation
        ? {
            locationName: '',
            abbreviation: '',
            street1: '',
            street2: '',
            city: '',
            state: '',
            zipcode: '',
            country: farm.address.country,
            type: LocationTypes.FARM,
          }
        : {
            locationName: editLocation.name,
            abbreviation: editLocation.abbreviation || '',
            street1: editLocation.address.street1,
            street2: editLocation.address.street2 || '',
            city: editLocation.address.city,
            state: editLocation.address.state,
            zipcode: editLocation.address.zipcode,
            country: editLocation.address.country,
            type: editLocation.type,
          },
    [isEdit, editLocation, farm.address.country],
  )

  const handleSubmitLocation = useCallback(
    async function (values: FormType, { resetForm }: FormikHelpers<FormType>) {
      const type: LocationTypes = values.type

      const data: LocalPickup = {
        id: editLocation?.id || '',
        farm: { id: farm.id, urlSafeSlug: farm.urlSafeSlug },
        address: {
          city: values.city,
          state: getShortState(values.state, values.country) || '',
          street1: values.street1,
          street2: values.street2,
          zipcode: values.zipcode,
          country: values.country,
          // this coordinate is only a temporary dummy value and the true geocoded coordinate should be obtained in a subsequent step
          coordinate: editLocation?.address.coordinate || { latitude: 0, longitude: 0 },
        },
        name: values.locationName,
        abbreviation: values.abbreviation,
        type,
        timezone: editLocation?.timezone || '',
      }

      if (isEdit && editLocation) {
        // Check that we are actually updating anything and warn the farmer of any important changes

        let result: Resolved<typeof validateLocationEdit>

        try {
          result = await validateLocationEdit(editLocation, data)
        } catch (error) {
          // This is meant to catch validation errors
          return Toast(errorToString(error))
        }

        // return here so we don't go back if something requires more attention or a popup was cancelled
        if (result.status === ReturnStates.DO_NOTHING) {
          return Toast('Nothing to update, going back')
        }

        // If the farmer agrees and there are changes then proceed to update
        if (result.status === ReturnStates.SAVE) {
          data.address = await geocode(data.address)
          data.timezone = await getTimeZone(data.address)
          await locationsCollection.update(result.data)
        }
      } else {
        data.address = await geocode(data.address)
        data.timezone = await getTimeZone(data.address)
        await addLocation(data, farm)
      }
      resetForm()
      navigation.navigate('Locations')
    },
    [editLocation, farm, navigation, isEdit],
  )

  const isFarmReady = !!farmId && farmId === farm.id

  const isEditDataReady = !loading && !!editLocation && !error

  const isReady = isFarmReady && (!isEdit || isEditDataReady)

  if (!isReady) {
    return (
      <AdminView
        customHeader={
          <EditHeader
            goBack={() => navigation.navigate('Locations')}
            title={isEdit ? 'Edit Location' : 'Add Location'}
            actionTitle="Back"
            onPress={() => navigation.navigate('Locations')}
          />
        }
      >
        <LoaderWithMessage loading={loading} icon="map-marked" title="Location not found">
          <Text>
            {!farmId || farmId !== farm.id
              ? 'Loading the farm data'
              : error ||
                'This location could not be loaded, please click the "X" in the upper right corner and go back and select a location from the list.'}
          </Text>
        </LoaderWithMessage>
      </AdminView>
    )
  }

  return (
    <Formik
      validationSchema={locationValidationSchema}
      initialValues={initialValues}
      onSubmit={handleSubmitLocation}
      enableReinitialize
    >
      {({
        touched,
        errors,
        values,
        isSubmitting,
        handleChange,
        handleBlur,
        setFieldValue,
        handleSubmit,
        setValues,
      }: FormikProps<FormType>) => {
        return (
          <AdminView
            customHeader={
              <EditHeader
                goBack={() => navigation.navigate('Locations')}
                title={isEdit && !!editLocation?.name ? `Edit Location - ${editLocation.name}` : 'Add Location'}
                dataId={editLocation?.id}
                actionTitle="Save"
                isLoading={isSubmitting}
                onPress={handleSubmit}
              />
            }
          >
            <View style={styles.formWrapper}>
              <FormInput
                onChangeText={handleChange('locationName')}
                onBlur={handleBlur('locationName')}
                value={values.locationName}
                label={<FormInputLabel label="Location Name" tooltipId={ToolTips.LOCATION_NAME} />}
                errorMessage={touched.locationName ? errors.locationName : ''}
              />
              <FormInput
                onChangeText={(val) => setFieldValue('abbreviation', val.toUpperCase())}
                maxLength={4}
                placeholder="LOC1"
                onBlur={handleBlur('abbreviation')}
                value={values.abbreviation}
                errorMessage={touched.abbreviation ? errors.abbreviation : ''}
                label={<FormInputLabel label="Label Abbreviation" />}
                helperText="A short version of the location name for packing labels."
              />
              <View style={styles.locationDropdownCont}>
                <FormPickerInput
                  value={values.type}
                  placeholder={null}
                  onValueChange={handleChange('type')}
                  items={typeDropdownItems}
                  label="Location Type"
                  errorMessage={touched.type ? errors.type : ''}
                />
              </View>
              <Text style={styles.label}>Address</Text>
              <GooglePlacesSearch
                placeholder="Search for your location"
                types="geocode"
                onSelectGooglePlace={(item) =>
                  setGooglePlace(item, (newValues) => setValues({ ...values, ...newValues }))
                }
                style={styles.placeSearch}
                contStyle={styles.placeSearchCont}
                inline
                country={farm.address.country}
              />
              <View style={styles.addressInputContainer} />

              <FormInput
                onChangeText={handleChange('street1')}
                onBlur={handleBlur('street1')}
                value={values.street1}
                label="Street Address"
                errorMessage={touched.street1 ? errors.street1 : ''}
              />
              <FormInput
                onChangeText={handleChange('street2')}
                onBlur={handleBlur('street2')}
                value={values.street2}
                label="Street 2"
                errorMessage={touched.street2 ? errors.street2 : ''}
              />
              <FormInput
                onChangeText={handleChange('city')}
                onBlur={handleBlur('city')}
                value={values.city}
                label="City"
                errorMessage={touched.city ? errors.city : ''}
              />
              <View>
                <FormStatePicker
                  country={values.country}
                  errorMessage={touched.state ? errors.state : ''}
                  value={values.state}
                  onValueChange={handleChange('state')}
                  onBlur={handleBlur('state')}
                />
              </View>

              <FormZipcodeInput
                country={values.country}
                value={values.zipcode}
                onChangeText={handleChange('zipcode')}
                onBlur={handleBlur('zipcode')}
                errorMessage={touched.zipcode ? errors.zipcode : ''}
              />
            </View>
          </AdminView>
        )
      }}
    </Formik>
  )
}

export const AddLocationScreen = withAdminAuth(AddLocationScreenComp, Permission.ProductSetup, AccessRight.Edit)

const styles = StyleSheet.create({
  placeSearch: {
    fontSize: 14,
    height: 30,
  },
  placeSearchCont: {
    color: Colors.shades[500],
    borderWidth: 1,
    borderColor: Colors.shades[100],
    borderRadius: 10,
    padding: 10,
  },
  formWrapper: {
    marginVertical: 20,
    width: '90%',
    maxWidth: 800,
    alignSelf: 'center',
  },
  label: {
    fontSize: 14,
    fontFamily: typography.body.medium,
    color: Colors.black,
    marginBottom: 4,
  },
  locationDropdownCont: {
    marginHorizontal: -10,
  },
  addressInputContainer: {
    height: 20,
  },
})
